import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Subject, firstValueFrom } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { LoginService } from '../../../../pages/login/login.service';
import { AppService, UserService } from '../../../../shared/service/user.service';
import { ConfirmService } from '../../../../shared/components/confirm/confirm.service';
import { ToastService } from '../../../../shared/components/toast/toast.service';

interface RunValue {
  idValue: string;
  actionType: string;
  taxCode?: string;
  requireLoginKey?: boolean;
}

@Injectable({ providedIn: 'root' })
export class CertificatedService {
  readonly errorSubject = new Subject<any>();
  readonly displaySubject = new Subject<any>();

  private readonly http: HttpClient;

  constructor(
    handler: HttpBackend,
    private confirmService: ConfirmService,
    private loginService: LoginService,
    private toastService: ToastService,

  ) {
    this.http = new HttpClient(handler);
  }

  async run(runValue: RunValue): Promise<void> {
    const url = `${window.location.origin}/api/rest/s1`;
    const data = {
      signingType: runValue.actionType,
      idValue: runValue.idValue,
      taxCode: runValue.taxCode || UserService.getUser().partyTaxId,
      responseContentType: 'application/json'
    };
    try {
      const testRes: any = await firstValueFrom(
        this.http.get('http://localhost:13369/test').pipe(timeout(1500))
      );

      if (testRes?.receiveDataFromBrowser !== 'Y') {
        return this.handleFallback(runValue);
      }

      await this.processSigning(data, runValue, url);
    } catch (err: any) {
      this.displaySubject.next('close');
      this.errorSubject.next(err);

      if (err?.error?.errorCode) {
        this.handleErrorDialog(err?.error);
      } else {
        this.displaySubject.next('signProgress');
        this.handleFallback(runValue);
      }

      throw err;
    }
  }

  private async processSigning(data: any, runValue: RunValue, url: string): Promise<void> {
    const signRes: any = await firstValueFrom(
      this.http.put(`${url}/iam-common/signing`, data, {
        headers: { 'x-csrf-token': AppService.get('MOQUI_SESSION_TOKEN') }
      })
    );

    this.displaySubject.next('open');

    const localSignRes: any = await firstValueFrom(
      this.http.post('http://localhost:13369/signing', signRes)
    );

    if (localSignRes?.success) {
      const finalRes: any = await firstValueFrom(
        this.http.post(`${url}/iam-common/complete-signing`, localSignRes, {
          headers: { 'x-csrf-token': AppService.get('MOQUI_SESSION_TOKEN') }
        })
      );

      if (finalRes?.idValue) {
        this.toastService.success();
        this.displaySubject.next('close');
      } else {
        this.handleErrorDialog(finalRes);
        throw new Error('Signing failed');
      }
    } else {
      await this.handleSignFail(runValue, localSignRes, url);
      throw new Error('Signing unsuccessful');
    }
  }

  private async handleSignFail(runValue: RunValue, response: any, url: string) {
    const input = {
      success: 'N',
      idValue: runValue.idValue,
      signingType: runValue.actionType
    };

    this.confirmService.setConfirmDialog({
      message: `[${response.errorCode}] ${response.errorMessage}`,
      header: 'Ký điện tử không thành công',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Đóng',
      acceptVisible: false
    });

    await firstValueFrom(
      this.http.post(`${url}/iam-common/complete-signing`, input, {
        headers: { 'x-csrf-token': AppService.get('MOQUI_SESSION_TOKEN') }
      })
    );
  }

  private handleErrorDialog(error: any) {
    this.confirmService.setConfirmDialog({
      message: `[${error?.errorCode}] ${error?.errors || error?.errorMessage}`,
      header: 'Ký điện tử không thành công',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Đóng',
      acceptVisible: false
    });
  }

  private handleFallback(runValue: RunValue) {
    this.loginService.getApiKey().subscribe((response) => {
      if (response.token) {
        this.errorSubject.next({status:0, message: 'Plugin not found'});
      }
    });
  }
}
